import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auth } from '../../services/auth';

const inputClasses =
  'mt-1 w-full rounded-xl border-0 bg-white/70 p-3 text-slate-800 shadow-sm ring-1 ring-emerald-100 backdrop-blur placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800/60 dark:text-slate-100 dark:ring-slate-700';

// Account profile editor + account deletion (customer-flow spec 2026-07-12).
// Rendered inside the Dashboard's Account section — no extra route.
export function ProfileSettings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(null); // null until the user opens the editor
  const [saved, setSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [error, setError] = useState(null);

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: auth.getProfile,
    retry: false,
    suspense: false,
  });

  const saveMutation = useMutation({
    mutationFn: (p) => auth.putProfile(p),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      setForm(null);
      setSaved(true);
    },
  });

  const handleDelete = async () => {
    setError(null);
    try {
      await auth.deleteAccount(deletePassword);
      navigate('/');
    } catch (err) {
      setError(
        err.response?.status === 401
          ? 'Incorrect password.'
          : err.response?.data?.detail || 'Could not delete your account. Try again.'
      );
    }
  };

  return (
    <div className="mt-4 space-y-4">
      {form === null ? (
        <div className="text-sm">
          <p className="text-slate-600 dark:text-slate-300">
            {profile?.address ? (
              <>
                {profile.address.split('\n').map((line, i) => (
                  <span key={i}>
                    {line}
                    <br />
                  </span>
                ))}
                {profile.phone}
              </>
            ) : (
              'No mailing address on file.'
            )}
          </p>
          {saved && (
            <p className="mt-2 font-semibold text-emerald-700 dark:text-emerald-400">
              Profile saved.
            </p>
          )}
          <button
            onClick={() => {
              setSaved(false);
              setForm({
                name: profile?.name || '',
                address: profile?.address || '',
                unit: profile?.unit || '',
                property_name: profile?.property_name || '',
                phone: profile?.phone || '',
                email: profile?.email || '',
              });
            }}
            className="mt-2 font-semibold text-emerald-700 underline dark:text-emerald-400"
          >
            Edit profile
          </button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveMutation.mutate(form);
          }}
          className="space-y-3 text-sm"
        >
          <div>
            <label htmlFor="profile-address" className="block font-semibold text-emerald-700 dark:text-emerald-400">
              Mailing address
            </label>
            <textarea
              id="profile-address"
              rows={2}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className={inputClasses}
            />
          </div>
          <div>
            <label htmlFor="profile-phone" className="block font-semibold text-emerald-700 dark:text-emerald-400">
              Phone
            </label>
            <input
              id="profile-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className={inputClasses}
            />
          </div>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="rounded-xl bg-emerald-600 px-4 py-2 font-bold text-white hover:bg-emerald-700"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setForm(null)}
              className="font-semibold text-slate-600 underline dark:text-slate-300"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="border-t border-emerald-100 pt-4 dark:border-slate-700">
        {!deleting ? (
          <button
            onClick={() => setDeleting(true)}
            className="text-sm font-semibold text-rose-600 underline dark:text-rose-400"
          >
            Delete my account
          </button>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-slate-700 dark:text-slate-200">
              This permanently deletes your account, your uploaded leases, and your
              reports. Enter your password to confirm.
            </p>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Password"
              className={inputClasses}
            />
            {error && <p className="text-rose-600 dark:text-rose-400">{error}</p>}
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                disabled={!deletePassword}
                className="rounded-xl bg-rose-600 px-4 py-2 font-bold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                Permanently delete
              </button>
              <button
                onClick={() => {
                  setDeleting(false);
                  setDeletePassword('');
                  setError(null);
                }}
                className="font-semibold text-slate-600 underline dark:text-slate-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfileSettings;
